import React from 'react';
import {
  Document,
  BLOCKS,
  INLINES,
  Block,
  Inline
} from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

import { IFullwidthHero20Fields } from '@/types/contentful';

import { getAssetFromOptions } from '../../../utils/contentful';
import { usePageDataContext } from '../../../context/PageData';

import PrivacyPolicyDisclaimer from '../../PrivacyPolicyDisclaimer';
import CtaWidget from '../../CtaWidget';
import { NewVideoPlayer } from '~/components/NewVideoPlayer';
import RichText from '~/components/RichText';

import { Asset as AssetComponent } from '../../AtomicComponents/Asset';
import { Asset as ContentfulAsset } from 'contentful';
import { AdditionalText } from '../AdditionalText';
import { Eyebrow } from '../../Eyebrow';
import { SocialProofBar } from '../../SocialProofBar';
import { TextWithOptions } from '~/components/Text/TextWithOptions';

interface Props extends IFullwidthHero20Fields {
  isReturning?: boolean;
}

export default function MobileView({
  mobileHeadingText,
  mobileHeadingTextOptions,
  mobileSubheadingText,
  mobileSubheadingTextRich,
  mobileSubheadingTextOptions,
  mobileImage,
  mobileImageOptions,
  additionalText,
  mobilePrivacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  privacyPolicyDisclaimer,
  videoModule,
  mobileCta,
  eyebrow,
  socialProofBar,
  isReturning,
  returningUserHeadingText,
  mobilePrivacyPolicyDisclaimerColor
}: Props) {
  const {
    cookies: { utm_source = '' } = {},
    query: { gl_content, g_adgroupid } = {},
    advocateUserData
  } = usePageDataContext();

  const hasVideo =
    videoModule?.fields?.vimeoId || videoModule?.fields?.mp4video;

  const SUBHEADING_RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return <>{children}</>;
    },
    [INLINES.HYPERLINK]: (node, children) => {
      const href = node.data.uri as string;
      return (
        <a
          href={href}
          className="underline transition-colors duration-200 ease-in-out hover:opacity-80"
        >
          {children}
        </a>
      );
    }
  };

  const renderSubheading = (text: string | Document | undefined) => {
    if (!text) return null;

    if (typeof text === 'string') {
      return <span>{text}</span>;
    }

    if (text && typeof text === 'object' && 'nodeType' in text) {
      return (
        <RichText
          data={text as Document}
          overrideOptions={SUBHEADING_RICH_TEXT_OVERRIDE}
        />
      );
    }

    return null;
  };

  return (
    <section
      className="block-container flex-col items-center desktop:hidden"
      data-testid="fullwidth-hero-mobile"
    >
      <div className="flex flex-col items-center justify-center self-stretch tablet:gap-6 tablet:px-10 tablet:pt-8">
        <div className="flex flex-col items-center justify-center gap-3 self-stretch">
          <div className="w-full self-stretch">
            {hasVideo ? (
              <NewVideoPlayer
                sysId={videoModule?.sys?.id}
                title={videoModule?.fields?.title || ''}
                theme={videoModule?.fields?.theme || 'light'}
                thumbnail={videoModule?.fields?.fallbackImage}
                video={videoModule?.fields?.mp4video}
                playAudio={!videoModule?.fields?.muted}
                autoplay={videoModule?.fields?.autoplay}
                loop={videoModule?.fields?.loop}
                inHero
              />
            ) : (
              <div data-testid="fullwidth-hero-mobile-background">
                <AssetComponent
                  asset={getAssetFromOptions(
                    gl_content,
                    utm_source,
                    g_adgroupid,
                    mobileImage as ContentfulAsset,
                    mobileImageOptions
                  )}
                  loading="eager"
                />
              </div>
            )}
          </div>
        </div>
        {eyebrow && (
          <Eyebrow
            eyebrowFields={eyebrow.fields}
            advocateData={advocateUserData}
          />
        )}
        <div className="mb-4 flex flex-col items-center justify-center gap-6 self-stretch px-5 pt-8 tablet:mb-0 tablet:justify-normal tablet:gap-4 tablet:p-0">
          <TextWithOptions
            as="h2"
            className="text-center text-[40px] leading-[115%] tablet:text-[52px]"
            keyword={gl_content}
            channel={utm_source}
            adGroupId={g_adgroupid}
            fallback={
              (isReturning
                ? returningUserHeadingText ?? mobileHeadingText
                : mobileHeadingText) || ''
            }
            options={mobileHeadingTextOptions}
            data-testid={'fullwidth-hero-headline-mobile'}
            module="Fullwidth Hero mobile heading"
          />
          {mobileSubheadingTextRich ? (
            <div
              className="text-center text-lg font-light leading-[115%]"
              data-testid="fullwidth-hero-subheading-mobile"
            >
              {renderSubheading(mobileSubheadingTextRich)}
            </div>
          ) : (
            mobileSubheadingText && (
              <TextWithOptions
                as="p"
                className="text-center text-lg leading-[115%]"
                data-testid="fullwidth-hero-subheading-mobile"
                keyword={gl_content}
                channel={utm_source}
                adGroupId={g_adgroupid}
                fallback={mobileSubheadingText ?? ''}
                options={mobileSubheadingTextOptions}
                module="Fullwidth Hero mobile subheading"
              />
            )
          )}
        </div>
        <div className="flex max-w-[350px] flex-col items-start gap-3 p-0">
          {mobileCta && (
            <CtaWidget
              {...mobileCta?.fields}
              id="fullwidth-hero-mobile-cta"
              sysId={mobileCta?.sys?.contentType?.sys?.id}
              module="Hero"
              isMobile={true}
              width={'100%'}
              responsive
            />
          )}
          {additionalText && <AdditionalText additionalText={additionalText} />}
          <div className="box-border">
            <PrivacyPolicyDisclaimer
              privacyPolicyDisclaimer={
                mobilePrivacyPolicyDisclaimer ?? privacyPolicyDisclaimer
              }
              displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
              colorHex={mobilePrivacyPolicyDisclaimerColor}
            />
          </div>
        </div>
      </div>
      {socialProofBar && <SocialProofBar {...socialProofBar.fields} />}
    </section>
  );
}
